import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { OmsExportPreset } from "../../lusca-search-api/generated-src";
import { OmsPresetsState, presetErrorMessage } from "../../hooks/useOmsPresets";
import { presetScopeLabel } from "./omsPresetEditing";

export default function OMSPresetToolbar({
  state,
  applied,
  disabled,
  saveDisabled = disabled,
  onApply,
  onSave,
  onManage,
  saveRef,
  selectRef,
  manageRef,
  modified = false,
  onCustom,
  editing = false,
}: {
  state: OmsPresetsState;
  applied?: OmsExportPreset;
  disabled: boolean;
  saveDisabled?: boolean;
  onApply: (presetId: string) => void;
  onSave: () => void;
  onManage: () => void;
  saveRef: React.RefObject<ButtonProps.Ref>;
  selectRef: React.RefObject<SelectProps.Ref>;
  manageRef: React.RefObject<ButtonProps.Ref>;
  modified?: boolean;
  onCustom: () => void;
  editing?: boolean;
}) {
  const busy = disabled || state.read.isLoading;
  return (
    <section className="oms-preset-section" aria-label="Export preset">
      <SpaceBetween size="s">
        <Header
          variant="h3"
          actions={
            <Button ref={manageRef} disabled={busy} onClick={onManage}>
              Manage presets
            </Button>
          }
        >
          {editing ? "Editing preset" : "Preset"}
        </Header>
        <div className="oms-preset-toolbar">
          <FormField label="Saved preset (optional)" stretch>
            <Select
              expandToViewport
              ref={selectRef}
              ariaLabel="Saved preset"
              placeholder="Choose a preset"
              filteringType="auto"
              filteringPlaceholder="Search presets"
              filteringAriaLabel="Search presets"
              filteringClearAriaLabel="Clear preset search"
              noMatch="No matching presets."
              statusType={
                state.list.isLoading || state.read.isLoading
                  ? "loading"
                  : "finished"
              }
              loadingText={
                state.read.isLoading ? "Applying preset" : "Loading presets"
              }
              disabled={busy || editing}
              selectedOption={
                applied
                  ? {
                      value: applied.presetId,
                      label: applied.name,
                      description: presetScopeLabel[applied.scope],
                    }
                  : null
              }
              options={[
                {
                  value: "custom",
                  label: "No preset",
                  description: "Keep the current rows and columns.",
                },
                ...(state.list.data || []).map((preset) => ({
                  value: preset.presetId,
                  label: preset.name,
                  description: presetScopeLabel[preset.scope],
                })),
              ]}
              onChange={({ detail }) => {
                if (detail.selectedOption.value === "custom") onCustom();
                else onApply(detail.selectedOption.value as string);
              }}
            />
          </FormField>
          <div className="oms-preset-toolbar__actions">
            <Button
              ref={saveRef}
              disabled={busy || saveDisabled}
              onClick={onSave}
            >
              {applied && (modified || editing)
                ? "Save changes"
                : "Save preset"}
            </Button>
          </div>
        </div>
        {applied && (
          <div className="oms-preset-section__status">
            <div className="oms-preset-context__status">
              <StatusIndicator type={modified ? "warning" : "success"}>
                {modified ? "Unsaved preset changes" : "Saved settings applied"}
              </StatusIndicator>
              <Box color="text-body-secondary">
                {presetScopeLabel[applied.scope]}
              </Box>
            </div>
            {modified && (
              <Box color="text-body-secondary">
                Your export uses these changes. The saved preset stays unchanged
                until you save.
              </Box>
            )}
          </div>
        )}
        {state.list.isError && (
          <Alert
            type="warning"
            header="Presets unavailable"
            action={
              <Button
                onClick={() => {
                  state.list.refetch().catch(() => undefined);
                }}
              >
                Retry presets
              </Button>
            }
          >
            You can still configure and create an export.
          </Alert>
        )}
        {state.read.isError && (
          <Alert
            type="error"
            header="Could not apply preset"
            action={
              <Button
                onClick={() => {
                  if (state.read.variables) onApply(state.read.variables);
                }}
              >
                Retry preset
              </Button>
            }
          >
            {presetErrorMessage(state.read.error)}
          </Alert>
        )}
      </SpaceBetween>
    </section>
  );
}
